import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {EmpionMultiSelect} from '../../../../../../components/EmpionMultiSelect/EmpionMultiSelect';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {SUPPORTED_TAGS} from '../../../../../../lib/constants';
import {handleCompanyDepartmentsOptions} from '../../../../../../utils/companyDepartmentsUtils/companyDepartmentsUtils';
import {handleCompanyLocationsOptions} from '../../../../../../utils/companyLocationsUtils/companyLocationsUtils';
import {CompanyDepartmentsResponse, CompanyProfile} from '../../../../profile/types';
import {InboxFilters} from '../../types/InboxTypes';

interface FiltersProps {
  companyLocations: CompanyProfile['locations'];
  companyDepartments: CompanyDepartmentsResponse['companyDepartments'];
  handleFilters: (event: any) => void;
  clearFilters: () => void;
  applyFilters: () => void;
  appliedFilters: InboxFilters;
}

export default function Filters({
  companyLocations,
  companyDepartments,
  handleFilters,
  clearFilters,
  applyFilters,
  appliedFilters,
}: FiltersProps) {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();

  const [isClearFilters, setIsClearFilters] = useState<boolean>(false);

  useEffect(() => {
    if (isClearFilters) {
      setIsClearFilters(false);
    }
  }, [isClearFilters]);

  const multiSelectStyles = {
    height: '46px',
    borderRadius: '8px',
    padding: '8px, 16px, 8px, 16px',
    width: {xs: '350px ', sm: '230px', md: '300px'},
  };

  const supportedTagsTranslated = SUPPORTED_TAGS.map((tag) => {
    return {
      id: tag.id,
      label: tag.label[currentLanguage],
    };
  });

  const clearMultiSelectFilters = () => {
    setIsClearFilters(true);
    clearFilters();
  };

  const areNoFiltersSelected = () => {
    return (
      appliedFilters.department.length === 0 && appliedFilters.location.length === 0 && appliedFilters.tags.length === 0
    );
  };

  return (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '15px',
        margin: '10px 0 40px 0',
        width: '100%',
      }}
    >
      <Grid item>
        <EmpionMultiSelect
          label={t('companyJobPostings.jobPostingsFilter.department')}
          options={handleCompanyDepartmentsOptions(companyDepartments, currentLanguage)}
          onChange={handleFilters}
          sx={multiSelectStyles}
          filteringFor={'department'}
          backgroundVariant='dark'
          isClearFilters={isClearFilters}
          aria-labelledby='Department'
        />
      </Grid>
      <Grid item>
        <EmpionMultiSelect
          label={t('companyJobPostings.jobPostingsFilter.location')}
          options={handleCompanyLocationsOptions(companyLocations)}
          onChange={handleFilters}
          sx={multiSelectStyles}
          filteringFor={'location'}
          backgroundVariant='dark'
          isClearFilters={isClearFilters}
        />
      </Grid>
      <Grid item>
        <EmpionMultiSelect
          label={t('companyJobPostings.jobPostingsFilter.tag')}
          options={supportedTagsTranslated}
          onChange={handleFilters}
          sx={multiSelectStyles}
          filteringFor={'tags'}
          backgroundVariant='dark'
          isClearFilters={isClearFilters}
        />
      </Grid>
      <Grid
        item
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <EmpionButton
          size='small'
          variant='text'
          disabled={areNoFiltersSelected()}
          sx={{
            fontSize: '14px',
            padding: '0px 6px 0px 6px',
          }}
          onClick={applyFilters}
        >
          {t('companyInbox.inboxButtons.applyFilter')}
        </EmpionButton>
        <EmpionButton
          size='small'
          disabled={areNoFiltersSelected()}
          sx={{
            fontSize: '14px',
            padding: '0px 6px 0px 6px',
            backgroundColor: greenColors[100],
            '&:hover': {
              backgroundColor: greenColors[600],
            },
            '&:disabled': {
              backgroundColor: greenColors[500],
            },
          }}
          onClick={clearMultiSelectFilters}
        >
          {t('companyInbox.inboxButtons.clearFilter')}
        </EmpionButton>
      </Grid>
    </Grid>
  );
}
